/**
 * Announcement bar — seamless marquee loop, dismiss, and header offset.
 * The bar lives in normal flow (not sticky). Speed is px/second; the loop
 * duration is derived from the real group width so the visual speed stays
 * constant regardless of how many messages are present.
 *
 * Header offset: the header is position:fixed with `top: var(--announcement-offset)`.
 * This file owns that variable: visible bar height minus scroll, recomputed on
 * scroll, on bar resize (fonts, height setting, wrapping) and on theme editor
 * section load/unload (the editor re-renders the bar, so no stale reference).
 */
(function () {
  /* The editor may re-inject this script with the section: re-init only */
  if (window.announcementBarInitAll) {
    window.announcementBarInitAll();
    return;
  }

  var root = document.documentElement;

  function currentBar() {
    return document.querySelector('[data-announcement-bar]');
  }

  function updateOffset() {
    var bar = currentBar();
    var offset = bar && bar.isConnected ? Math.max(0, bar.offsetHeight - window.scrollY) : 0;
    root.style.setProperty('--announcement-offset', offset + 'px');
  }

  var ticking = false;
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      updateOffset();
      ticking = false;
    });
  }, { passive: true });

  var barObserver = window.ResizeObserver ? new ResizeObserver(updateOffset) : null;
  if (!barObserver) window.addEventListener('resize', updateOffset);

  function initBar(bar) {
    if (bar.hasAttribute('data-announcement-ready')) return;
    bar.setAttribute('data-announcement-ready', '');

    var sectionId = bar.getAttribute('data-section-id');
    var storageKey = 'announcement-dismissed-' + sectionId;

    /* Dismissed earlier this session → remove without flashing */
    if (bar.classList.contains('announcement-bar--dismissible')) {
      try {
        if (sessionStorage.getItem(storageKey) === '1') {
          bar.remove();
          updateOffset();
          return;
        }
      } catch (e) { /* sessionStorage unavailable — show the bar */ }
    }

    if (barObserver) barObserver.observe(bar);

    var track = bar.querySelector('[data-announcement-track]');
    var group = bar.querySelector('[data-announcement-group]');
    var viewport = bar.querySelector('.announcement-bar__viewport');
    var isScrolling = bar.classList.contains('announcement-bar--scroll');

    if (isScrolling && track && group && viewport) {
      var speed = parseFloat(bar.getAttribute('data-speed')) || 60;

      /*
       * Build the loop: repeat the group enough times so that ONE "unit" is at
       * least as wide as the visible viewport, then place two identical units in
       * the track. translateX(0 → -50%) then shifts exactly one unit, which is
       * seamless because the unit fully covers the viewport (no empty gap).
       */
      function buildLoop() {
        /* Reset: keep only the original group */
        var groups = track.querySelectorAll('[data-announcement-group]');
        for (var i = groups.length - 1; i >= 1; i--) {
          groups[i].remove();
        }

        var groupWidth = group.offsetWidth;
        var viewportWidth = viewport.offsetWidth;
        if (groupWidth <= 0) return;

        /* Copies needed for one unit to cover the viewport (capped for safety) */
        var perUnit = Math.max(1, Math.ceil(viewportWidth / groupWidth));
        perUnit = Math.min(perUnit, 50);

        /* Two identical units → total groups = perUnit * 2 (we already have 1) */
        var totalGroups = perUnit * 2;
        var frag = document.createDocumentFragment();
        for (var j = 1; j < totalGroups; j++) {
          var clone = group.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          frag.appendChild(clone);
        }
        track.appendChild(frag);

        var unitWidth = groupWidth * perUnit;
        track.style.setProperty('--announcement-duration', (unitWidth / speed).toFixed(2) + 's');
      }

      buildLoop();

      /* Recompute once fonts are loaded (widths change as Urbanist swaps in) */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(buildLoop);
      }

      /* Rebuild on viewport width changes. Observing the viewport (flex:1) is
         safe: appending clones to the track does not change the viewport size,
         so this never re-enters. */
      if (window.ResizeObserver) {
        new ResizeObserver(buildLoop).observe(viewport);
      } else {
        window.addEventListener('resize', buildLoop);
      }
    }

    /* Dismiss button */
    var closeBtn = bar.querySelector('[data-announcement-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        bar.remove();
        updateOffset();
        try { sessionStorage.setItem(storageKey, '1'); } catch (e) { /* no-op */ }
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-announcement-bar]').forEach(initBar);
    updateOffset();
  }

  window.announcementBarInitAll = initAll;
  initAll();

  /* Theme editor: the section is re-rendered on every setting change */
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function () {
    /* Unload fires before the old node is removed → recompute next frame */
    requestAnimationFrame(updateOffset);
  });
  document.addEventListener('shopify:section:reorder', updateOffset);
})();
